import React, { useMemo, useState } from "react";
import { usePairData, pairId } from "../hooks/usePairData";
import { samePair } from "../lib/pairs";
import { SPREAD_SCREEN, PREF_PAIRS, SCREEN_ASOF } from "../lib/spreadScreen";

const LONG_C = "#0a7c59", SHORT_C = "#c0392b";

// ADF 유의수준 라벨 (음수일수록 강한 회귀)
const adfSig = s => s < -3.43 ? "1%" : s < -2.86 ? "5%" : s < -2.57 ? "10%" : "약";
// 종합 등급: 안정성(회귀강도) + |z|(극단) + β(양수·정상) 합산
function setupGrade(s, z, beta) {
  if (beta != null && beta <= 0) return { t: "제외", c: "#bbb", bg: "#f5f5f5" };  // β음수=페어 아님
  const az = Math.abs(z);
  if (s < -3.43 && az >= 2) return { t: "1등급", c: "#fff", bg: "#0a7c59" };       // 강한회귀+신호
  if (s < -2.86 && az >= 1.5) return { t: "2등급", c: "#fff", bg: "#b8860b" };      // 유의+근접
  return { t: "3등급", c: "#999", bg: "#eee" };                                     // 관찰
}

function Row({ d, added, onAdd }) {
  return (
    <tr style={{ borderTop: "1px solid #f2f2f2" }}>
      <td style={{ padding: "6px 8px", color: "#aaa", fontSize: 11 }}>{d.t}</td>
      <td style={{ padding: "6px 8px", fontSize: 12 }}>
        <span style={{ color: "#1f3a93", fontWeight: 600 }}>{d.a}</span>
        <span style={{ color: "#ccc", margin: "0 4px" }}>/</span>
        <span style={{ color: SHORT_C, fontWeight: 600 }}>{d.b}</span>
      </td>
      <td style={{ padding: "6px 8px", textAlign: "right", color: "#888", fontSize: 11 }}>{d.beta != null ? d.beta.toFixed(2) : "-"}</td>
      <td style={{ padding: "6px 8px", textAlign: "right", color: "#888", fontSize: 11 }}>
        {d.s}<span style={{ color: "#bbb", fontSize: 9, marginLeft: 2 }}>{adfSig(d.s)}</span>
      </td>
      <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 800, fontSize: 12, color: d.z < 0 ? LONG_C : SHORT_C, opacity: Math.abs(d.z) >= 2 ? 1 : 0.5 }}>
        {Math.abs(d.z) >= 2 && <span title="±2σ 돌파 (신호)" style={{ marginRight: 3 }}>●</span>}
        {d.z > 0 ? "+" : ""}{d.z.toFixed(2)}
      </td>
      <td style={{ padding: "6px 8px", textAlign: "center" }}>
        {(() => { const g = setupGrade(d.s, d.z, d.beta); return (
          <span style={{ fontSize: 10, fontWeight: 800, color: g.c, background: g.bg, borderRadius: 5, padding: "2px 6px", whiteSpace: "nowrap" }}>{g.t}</span>
        ); })()}
      </td>
      <td style={{ padding: "6px 8px", textAlign: "center" }}>
        {added ? <span style={{ color: "#0a7c59", fontSize: 13 }}>✓</span> : (
          <button onClick={() => onAdd(d)} title="차트북에 추가"
            style={{ border: "none", background: "#eef1f8", color: "#1f3a93", borderRadius: 6, cursor: "pointer", fontWeight: 800, padding: "2px 8px", fontSize: 12 }}>+</button>
        )}
      </td>
    </tr>
  );
}

function ScreenTable({ title, color, rows, isAdded, onAdd, note, loading }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 10, overflow: "hidden", minWidth: 0 }}>
      <div style={{ padding: "9px 12px", background: color, color: "#fff", fontSize: 13, fontWeight: 800 }}>
        {title} <span style={{ opacity: 0.8, fontWeight: 500 }}>({rows.length})</span>
      </div>
      <div style={{ padding: "4px 10px 2px", fontSize: 10, color: "#aaa" }}>{note}</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#fafafa", color: "#999", fontSize: 10 }}>
              <th style={{ padding: "5px 8px", textAlign: "left" }}>테마</th>
              <th style={{ padding: "5px 8px", textAlign: "left" }}>롱 / 숏</th>
              <th style={{ padding: "5px 8px", textAlign: "right" }}>β</th>
              <th style={{ padding: "5px 8px", textAlign: "right" }}>안정성</th>
              <th style={{ padding: "5px 8px", textAlign: "right" }}>z(라이브)</th>
              <th style={{ padding: "5px 8px", textAlign: "center" }}>등급</th>
              <th style={{ padding: "5px 8px", textAlign: "center" }}>+</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0
              ? <tr><td colSpan={7} style={{ padding: 16, textAlign: "center", color: "#bbb", fontSize: 12 }}>{loading ? "라이브 계산 중…" : "해당 없음"}</td></tr>
              : rows.map((d, i) => <Row key={d.ca + d.cb + i} d={d} added={isAdded(d)} onAdd={onAdd} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SpreadScreener({ existingPairs, onAddPair }) {
  // 스냅샷 z가 극단 근처인 후보만 라이브 재계산 (방향별 top~25)
  const mkCand = d => ({ longTicker: d.ca, longName: d.a, longMarket: d.ma, shortTicker: d.cb, shortName: d.b, shortMarket: d.mb, _t: d.t, _s: d.s });
  const screenerCand = useMemo(() => {
    const L = SPREAD_SCREEN.filter(d => d.z < -0.5).sort((a, b) => a.z - b.z).slice(0, 35);
    const S = SPREAD_SCREEN.filter(d => d.z > 0.5).sort((a, b) => b.z - a.z).slice(0, 35);
    const seen = new Set(), out = [];
    for (const d of [...L, ...S]) {
      const k = d.ca + "_" + d.cb;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(mkCand(d));
    }
    return out;
  }, []);
  const prefCand = useMemo(() => PREF_PAIRS.map(mkCand), []);
  const allCand = useMemo(() => {
    const seen = new Set(), out = [];
    for (const c of [...screenerCand, ...prefCand]) {
      const k = c.longTicker + "_" + c.shortTicker;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(c);
    }
    return out;
  }, [screenerCand, prefCand]);

  const [range, setRange] = useState("5y");
  const { data, loading } = usePairData(allCand, range, "1wk");   // 라이브 z (주봉, 룩백 토글)

  const toRow = c => {
    const r = data[pairId(c)];
    return {
      t: c._t, a: c.longName, b: c.shortName, ca: c.longTicker, cb: c.shortTicker, ma: c.longMarket, mb: c.shortMarket,
      s: c._s, beta: r ? r.beta : null, z: r ? r.z : null,
    };
  };
  const rows = screenerCand.map(toRow).filter(r => r.z != null);
  const longs = rows.filter(r => r.z < 0).sort((a, b) => a.z - b.z).slice(0, 10);
  const shorts = rows.filter(r => r.z > 0).sort((a, b) => b.z - a.z).slice(0, 10);
  const prefRows = prefCand.map(toRow).filter(r => r.z != null).sort((a, b) => Math.abs(b.z) - Math.abs(a.z));

  const toPair = d => ({ longs: [{ ticker: d.ca, name: d.a, market: d.ma }], shorts: [{ ticker: d.cb, name: d.b, market: d.mb }] });
  const isAdded = d => existingPairs.some(p => samePair(p, toPair(d)));
  const add = d => onAddPair(toPair(d));

  return (
    <div style={{ marginTop: 30, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>
          🎯 스프레드 스크리너 <span style={{ fontSize: 12, color: "#aaa", fontWeight: 500 }}>top 10</span>
          {loading && <span style={{ fontSize: 11, color: "#aaa", fontWeight: 500, marginLeft: 8 }}>라이브 계산 중…</span>}
        </h3>
        <div style={{ display: "flex", border: "1px solid #e2e2e2", borderRadius: 8, overflow: "hidden" }}>
          {[["6mo", "6개월"], ["1y", "1년"], ["3y", "3년"], ["5y", "5년"]].map(([v, lab]) => (
            <button key={v} onClick={() => setRange(v)} style={{
              padding: "5px 11px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
              background: range === v ? "#1a1a2e" : "#fff", color: range === v ? "#fff" : "#888",
            }}>{lab}</button>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#aaa", marginBottom: 12 }}>
        회귀성 유의(안정성 &lt; −2.86) 후보의 <b>z·β를 오늘 기준 라이브 재계산</b>(주봉 · 룩백 {range}) → z 순 top10. <b>● = ±2σ 돌파(신호)</b>, 흐린 건 ±2 미만(관찰). 등급=안정성·\|z\|·β 종합. 안정성 옆 %=유의수준. 안정성·후보선정은 엑셀 스냅샷({SCREEN_ASOF}).
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
        <ScreenTable title="🔵 LONG Spread 후보" color={LONG_C} rows={longs} isAdded={isAdded} onAdd={add} loading={loading}
          note="z < −2 : 롱레그 저평가 → 롱스프레드(롱 사고/숏 치기) 유리" />
        <ScreenTable title="🔴 SHORT Spread 후보" color={SHORT_C} rows={shorts} isAdded={isAdded} onAdd={add} loading={loading}
          note="z > +2 : 스프레드 과확대 → 되돌림(숏스프레드) 후보" />
      </div>

      {/* 우선주 페어 (보통-우선, 별도) */}
      <div style={{ marginTop: 16 }}>
        <ScreenTable title="⭐ 우선주 페어 (보통-우선)" color="#6d5a9c" rows={prefRows} isAdded={isAdded} onAdd={add} loading={loading}
          note="같은 기업 보통주(롱)/우선주(숏). 구조적 고안정성이라 안정성 필터와 무관하게 전부 포함 · |z| 큰 순 · ●=±2σ 돌파" />
      </div>
    </div>
  );
}
